import { splitManuscriptWireParagraphs } from "./manuscriptPassageBreakpoints";
import { EDITOR_PLAIN_TEXT_BLOCK_SEPARATOR } from "./editorPlainText";
import { manuscriptLocatedSpanCoversPassage } from "./manuscriptPassageMatch";

/** ProseMirror / wire editor selection bounds. */
export type ManuscriptEditorSelectionHint = {
  documentId: string;
  pmFrom?: number;
  pmTo?: number;
  plainTextStart?: number;
  plainTextEnd?: number;
};

/**
 * Canonical selection replace target: captured once at highlight/draft time.
 * Paragraph boundaries use single `\n` (TipTap textBetween), not markdown layout.
 */
export type ManuscriptSelectionTarget = {
  documentId: string;
  passageText: string;
  paragraphs: string[];
  pmFrom?: number;
  pmTo?: number;
  plainTextStart?: number;
  plainTextEnd?: number;
  /** Frozen markdown span from draft-time PM mapping when available. */
  markdownRange?: { start: number; end: number };
  markdownQuotedText?: string;
  anchorId?: string;
};

export function buildManuscriptSelectionTarget(params: {
  documentId: string;
  passageText: string;
  editorSelectionHint?: ManuscriptEditorSelectionHint | null;
  markdownRange?: { start: number; end: number };
  markdownQuotedText?: string;
  anchorId?: string;
}): ManuscriptSelectionTarget | null {
  const documentId = String(params.documentId ?? "").trim();
  const passageText = String(params.passageText ?? "").trim();
  if (!documentId || passageText.length < 8) return null;

  const hint = params.editorSelectionHint ?? undefined;
  const paragraphs = splitManuscriptWireParagraphs(passageText);
  const pmFrom = finiteNumber(hint?.pmFrom);
  const pmTo = finiteNumber(hint?.pmTo);
  const plainTextStart = finiteNumber(hint?.plainTextStart);
  const plainTextEnd = finiteNumber(hint?.plainTextEnd);

  return {
    documentId,
    passageText,
    paragraphs,
    ...(pmFrom !== undefined && pmTo !== undefined && pmTo > pmFrom
      ? { pmFrom, pmTo }
      : {}),
    ...(plainTextStart !== undefined && plainTextEnd !== undefined && plainTextEnd > plainTextStart
      ? { plainTextStart, plainTextEnd }
      : {}),
    ...(params.markdownRange ? { markdownRange: params.markdownRange } : {}),
    ...(params.markdownQuotedText ? { markdownQuotedText: params.markdownQuotedText } : {}),
    ...(params.anchorId ? { anchorId: params.anchorId } : {}),
  };
}

export function selectionTargetFromWire(params: {
  documentId?: string | null;
  clientPassage?: string | null;
  editorSelectionHint?: ManuscriptEditorSelectionHint | null;
}): ManuscriptSelectionTarget | null {
  const passageText = String(params.clientPassage ?? "").trim();
  const documentId =
    String(params.documentId ?? "").trim()
    || String(params.editorSelectionHint?.documentId ?? "").trim();
  if (!documentId || passageText.length < 8) return null;
  return buildManuscriptSelectionTarget({
    documentId,
    passageText,
    editorSelectionHint: params.editorSelectionHint,
  });
}

export function parseManuscriptSelectionTarget(raw: unknown): ManuscriptSelectionTarget | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  const documentId = String(record.documentId ?? "").trim();
  const passageText = String(record.passageText ?? record.passage ?? "").trim();
  if (!documentId || passageText.length < 8) return null;

  const paragraphs = Array.isArray(record.paragraphs)
    ? record.paragraphs
      .map((entry) => String(entry ?? "").trim())
      .filter((entry) => entry.length > 0)
    : splitManuscriptWireParagraphs(passageText);

  const markdownRange = parseRange(record.markdownRange);
  const pmFrom = finiteNumber(record.pmFrom);
  const pmTo = finiteNumber(record.pmTo);
  const plainTextStart = finiteNumber(record.plainTextStart);
  const plainTextEnd = finiteNumber(record.plainTextEnd);

  return {
    documentId,
    passageText,
    paragraphs,
    ...(pmFrom !== undefined && pmTo !== undefined && pmTo > pmFrom ? { pmFrom, pmTo } : {}),
    ...(plainTextStart !== undefined && plainTextEnd !== undefined && plainTextEnd > plainTextStart
      ? { plainTextStart, plainTextEnd }
      : {}),
    ...(markdownRange ? { markdownRange } : {}),
    ...(typeof record.markdownQuotedText === "string" && record.markdownQuotedText.trim()
      ? { markdownQuotedText: record.markdownQuotedText.trim() }
      : {}),
    ...(typeof record.anchorId === "string" && record.anchorId.trim()
      ? { anchorId: record.anchorId.trim() }
      : {}),
  };
}

export function selectionTargetToEditorHint(
  target: ManuscriptSelectionTarget,
): ManuscriptEditorSelectionHint {
  return {
    documentId: target.documentId,
    ...(typeof target.pmFrom === "number" ? { pmFrom: target.pmFrom } : {}),
    ...(typeof target.pmTo === "number" ? { pmTo: target.pmTo } : {}),
    ...(typeof target.plainTextStart === "number" ? { plainTextStart: target.plainTextStart } : {}),
    ...(typeof target.plainTextEnd === "number" ? { plainTextEnd: target.plainTextEnd } : {}),
  };
}

export function replaceSpanCoversSelectionTarget(
  locatedText: string,
  target: ManuscriptSelectionTarget,
): boolean {
  return manuscriptLocatedSpanCoversPassage(locatedText, target.passageText);
}

/** Join wire paragraphs back to canonical passage text. */
export function joinManuscriptWireParagraphs(paragraphs: string[]): string {
  return paragraphs
    .map((entry) => String(entry ?? "").trim())
    .filter((entry) => entry.length > 0)
    .join(EDITOR_PLAIN_TEXT_BLOCK_SEPARATOR);
}

export function readSelectionTargetFromRecord(
  record: Record<string, unknown> | null | undefined,
): ManuscriptSelectionTarget | null {
  if (!record) return null;
  const direct = parseManuscriptSelectionTarget(record.selectionTarget);
  if (direct) return direct;
  const passage = String(record.passage ?? record.rewriteTargetText ?? "").trim();
  const hint = record.editorSelectionHint;
  return buildManuscriptSelectionTarget({
    documentId: String(record.documentId ?? "").trim(),
    passageText: passage,
    editorSelectionHint:
      hint && typeof hint === "object" && !Array.isArray(hint)
        ? (hint as ManuscriptEditorSelectionHint)
        : undefined,
  });
}

/** Prefer the newest assistant draft that captured a frozen selection target. */
export function readFrozenSelectionTargetFromConversation(
  conversation: Array<{ role?: string; metadata?: Record<string, unknown> }>,
): ManuscriptSelectionTarget | null {
  for (let i = conversation.length - 1; i >= 0; i -= 1) {
    const msg = conversation[i];
    if (msg?.role !== "assistant") continue;
    const metadata = msg.metadata;
    if (!metadata) continue;
    const fromRoot = readSelectionTargetFromRecord(metadata);
    if (fromRoot) return fromRoot;
    const fromPending = readSelectionTargetFromRecord(
      metadata.pendingManuscriptDraft as Record<string, unknown> | undefined,
    );
    if (fromPending) return fromPending;
    const placementRaw = metadata.placementArtifact;
    if (placementRaw && typeof placementRaw === "object" && !Array.isArray(placementRaw)) {
      const fromPlacement = readSelectionTargetFromRecord(placementRaw as Record<string, unknown>);
      if (fromPlacement) return fromPlacement;
    }
  }
  return null;
}

function finiteNumber(value: unknown): number | undefined {
  const n = Number(value);
  return Number.isFinite(n) ? Math.floor(n) : undefined;
}

function parseRange(raw: unknown): { start: number; end: number } | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const start = finiteNumber((raw as { start?: unknown }).start);
  const end = finiteNumber((raw as { end?: unknown }).end);
  if (start === undefined || end === undefined || end <= start) return null;
  return { start, end };
}
